import React from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { isNil, uniq } from 'lodash'
import { Box } from '@mui/material'
import { CardTable } from '../../components/datatable/NewTable'
import Select from '../../components/forms/Select'
import { darkBlue } from '../../utils/constants/ColorTheme'
import { DENSE_INPUT_SX } from '../../utils/constants/Theme'
import { ShapePointPrelLight } from '../shapes/StationShapes'
import { shallowEqual, useSelector } from 'react-redux'

const StationsPointPrelTable = ({
    stations = [],
    pointsPrel = [],
    points = [],
    dataType = undefined,
    onChange = () => {},
    disabled = false,
    actions = undefined,
    hideNull = false,
    codeOptions = undefined,
    selectedCodes = [],
    onChangeCodes = () => {},
    coefficients = [],
    defaultCoefficient,
    coefficientOptions = [],
    onChangeCoefficients,
}) => {
    const {
        citiesIndex,
    } = useSelector(store => ({
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    if (hideNull && !stations.length) {
        return null
    }

    const withCoefficient = !isNil(onChangeCoefficients)

    const getCodeCell = station => {
        if (isNil(codeOptions)) {
            return station.code
        }
        const options = uniq([station.code, ...(codeOptions[station.id] ?? [])]).map(code => ({ code, name: code }))
        return (
            <Box sx={DENSE_INPUT_SX}>
                <Select
                    value={selectedCodes.find(c => c.idStation === station.id)?.code ?? station.code}
                    options={options}
                    keyValue='code'
                    keyLabel='name'
                    noNullValue
                    onChange={v => onChangeCodes([
                        ...selectedCodes.filter(c => c.idStation !== station.id),
                        ...(v && v !== station.code ? [{ idStation: station.id, code: v }] : []),
                    ])}
                    disabled={disabled}
                />
            </Box>
        )
    }

    return (
        <CardTable
            title={i18n.stations}
            actions={actions}
            rows={stations.map(station => ({
                code: getCodeCell(station),
                name: station.name,
                city: citiesIndex[station?.townCode]?.name,
                pointPrelCode: (
                    <Box sx={DENSE_INPUT_SX}>
                        <Select
                            value={pointsPrel.find(p => p.idStation === station.id)?.code}
                            options={points.filter(p => p.idStation === station.id && (!dataType || p.typeId === dataType))}
                            keyValue='code'
                            keyLabel='name'
                            onChange={v => onChange([
                                ...pointsPrel.filter(p => p.idStation !== station.id),
                                ...(v ? [{ idStation: station.id, code: v }] : []),
                            ])}
                            disabled={disabled}
                        />
                    </Box>
                ),
                ...(withCoefficient ? {
                    heightCoefficient: (
                        <Box sx={DENSE_INPUT_SX}>
                            <Select
                                value={coefficients.find(c => c.idStation === station.id)?.coefficient ?? defaultCoefficient}
                                options={coefficientOptions}
                                keyValue='code'
                                keyLabel='name'
                                noSort
                                noNullValue
                                onChange={v => onChangeCoefficients([
                                    ...coefficients.filter(c => c.idStation !== station.id),
                                    ...(isNil(v) ? [] : [{ idStation: station.id, coefficient: v }]),
                                ])}
                                disabled={disabled}
                            />
                        </Box>
                    ),
                } : {}),
            }))}
            headers={['code', 'name', 'city', 'pointPrelCode', ...(withCoefficient ? ['heightCoefficient'] : [])]}
            color={darkBlue}
            rowsPerPageOptions={[{ value: 5, label: '5' }]}
        />
    )
}

StationsPointPrelTable.propTypes = {
    stations: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        code: PropTypes.string,
        name: PropTypes.string,
    })),
    pointsPrel: PropTypes.arrayOf(PropTypes.shape({
        idStation: PropTypes.number,
        code: PropTypes.string,
    })),
    points: PropTypes.arrayOf(ShapePointPrelLight),
    dataType: PropTypes.number,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    actions: PropTypes.arrayOf(PropTypes.shape({
        icon: PropTypes.string,
        onClick: PropTypes.func,
        color: PropTypes.string,
        tooltip: PropTypes.string,
    })),
    hideNull: PropTypes.bool,
    codeOptions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
    selectedCodes: PropTypes.arrayOf(PropTypes.shape({
        idStation: PropTypes.number,
        code: PropTypes.string,
    })),
    onChangeCodes: PropTypes.func,
    coefficients: PropTypes.arrayOf(PropTypes.shape({
        idStation: PropTypes.number,
        coefficient: PropTypes.number,
    })),
    defaultCoefficient: PropTypes.number,
    coefficientOptions: PropTypes.arrayOf(PropTypes.shape({
        code: PropTypes.number,
        name: PropTypes.string,
    })),
    onChangeCoefficients: PropTypes.func,
}

export default StationsPointPrelTable